import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import useTrainingsApi from '../../list/hooks/useTrainingsApi'

const monthNames = {
	pl: [
		'styczeń',
		'luty',
		'marzec',
		'kwiecień',
		'maj',
		'czerwiec',
		'lipiec',
		'sierpień',
		'wrzesień',
		'październik',
		'listopad',
		'grudzień',
	],
	en: [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	],
}

export default function useTrainingsCalendarPage() {
	const { i18n } = useTranslation()
	const navigate = useNavigate()
	const { getAllTrainings } = useTrainingsApi()
	const [startDate, setStartDate] = useState(moment().startOf('month'))
	const [showDatePicker, setShowDatePicker] = useState(false)
	const [tempDate, setTempDate] = useState(moment().startOf('month').format('YYYY-MM-DD'))
	const [trainings, setTrainings] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const datePickerRef = useRef(null)
	const inputRef = useRef(null)

	const { start, end } = useMemo(() => ({
		start: moment(startDate).startOf('month').format('YYYY-MM-DD'),
		end: moment(startDate).endOf('month').format('YYYY-MM-DD'),
	}), [startDate])

	const loadData = useCallback(async () => {
		setLoading(true)
		setError('')

		try {
			const result = await getAllTrainings({
				limit: 500,
				sortColumn: 'trainingDate',
				sortDirection: 'asc',
			})

			if (result?.error) {
				setTrainings([])
				setError(result.error)
				return
			}

			const startMoment = moment(start, 'YYYY-MM-DD', true)
			const endMoment = moment(end, 'YYYY-MM-DD', true)
			const safeItems = Array.isArray(result?.data) ? result.data : []
			const filteredItems = safeItems.filter(item => {
				const itemStart = moment(item?.trainingDate, moment.ISO_8601, true)
				const itemEnd = moment(item?.trainingEndDate || item?.trainingDate, moment.ISO_8601, true)

				if (!itemStart.isValid() || !itemEnd.isValid()) return false
				if (startMoment.isValid() && itemEnd.isBefore(startMoment, 'day')) return false
				if (endMoment.isValid() && itemStart.isAfter(endMoment, 'day')) return false
				return true
			})

			setTrainings(filteredItems)
		} catch (error) {
			console.error('useTrainingsCalendarPage.loadData error:', error)
			setTrainings([])
			setError(error?.message || 'Failed to load trainings calendar')
		} finally {
			setLoading(false)
		}
	}, [end, getAllTrainings, start])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		const handleClickOutside = event => {
			if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
				setShowDatePicker(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	useEffect(() => {
		setTempDate(startDate.format('YYYY-MM-DD'))
	}, [startDate])

	const handlePrevious = useCallback(() => {
		setStartDate(prevDate => moment(prevDate).subtract(1, 'month'))
	}, [])

	const handleNext = useCallback(() => {
		setStartDate(prevDate => moment(prevDate).add(1, 'month'))
	}, [])

	const handleDateChange = useCallback(newDate => {
		setStartDate(moment(newDate).startOf('month'))
	}, [])

	const handleToday = useCallback(() => {
		setStartDate(moment().startOf('month'))
	}, [])

	const handleDatePickerChange = useCallback(event => {
		const newDate = event.target.value
		setTempDate(newDate)

		if (newDate) {
			handleDateChange(newDate)
			setShowDatePicker(false)
		}
	}, [handleDateChange])

	const handleDatePickerBlur = useCallback(() => {
		setTimeout(() => {
			setShowDatePicker(false)
		}, 200)
	}, [])

	const handleToggleDatePicker = useCallback(() => {
		setShowDatePicker(prev => !prev)
	}, [])

	const handleAdd = useCallback(() => {
		navigate('/centrumSzkolen/szkolenia/add', { state: { anchorDate: startDate.toISOString() } })
	}, [navigate, startDate])

	const monthLocale = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('pl') ? 'pl' : 'en'
	const monthIndex = moment(startDate).month()
	const year = moment(startDate).year()
	const monthLabel = monthNames[monthLocale][monthIndex] || ''
	const dateDisplay = monthLabel ? `${monthLabel.charAt(0).toUpperCase()}${monthLabel.slice(1)} ${year}` : ''

	return {
		dateDisplay,
		datePickerRef,
		error,
		handleAdd,
		handleDatePickerBlur,
		handleDatePickerChange,
		handleNext,
		handlePrevious,
		handleToday,
		handleToggleDatePicker,
		inputRef,
		loading,
		showDatePicker,
		startDate,
		tempDate,
		trainings,
	}
}
